import { ref, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  getTokenDetail,
  getTokenTransfers,
} from '@/config/apiServer/scanner'
import type { TokenDetailApiData, TokenTransferItem } from '@/config/apiServer/scanner'

const defaultIcon = require('../assets/img/common/baseCoin.png') as string

export interface TokenDetailDisplay {
  address: string
  symbol: string
  name?: string
  decimals?: number
  total?: string | number
  total_formatted?: string | number
  deploy_time?: string | number
  icon: string
  nickname?: string
  holders?: number
}

export interface TokenTxRow {
  hash: string
  tx_hash: string
  from: string
  to: string
  block_time?: string | number
  value?: string | number
  value_formatted?: string | number
  token_symbol?: string
  success: boolean
  is_para: boolean
}

type RouteTokenQuery = { tokenAdr?: unknown; adr?: unknown }

function readTokenQuery(q: RouteTokenQuery): { token: string; adr: string } {
  const t = q.tokenAdr
  const a = q.adr
  return {
    token: typeof t === 'string' ? t : '',
    adr: typeof a === 'string' ? a : '',
  }
}

export default function useTokenDetail(): {
  tab: ReturnType<typeof ref<number>>
  searchAddress: ReturnType<typeof ref<string>>
  propsAdr: ReturnType<typeof ref<string>>
  tokenAddress: ReturnType<typeof ref<string>>
  tokenName: ReturnType<typeof ref<string>>
  TokenInfo: ReturnType<typeof ref<TokenDetailDisplay>>
  detailLoading: ReturnType<typeof ref<boolean>>
  txLoading: ReturnType<typeof ref<boolean>>
  pages: { currentPage: number; pageSize: number; total: number }
  Trades: ReturnType<typeof ref<TokenTxRow[]>>
  searchAdr: () => void
  tabChange: (t: number) => void
  pageChange: (page: number) => void
  sizeChange: (size: number) => void
  clearAddrFilter: () => void
} {
  const route = useRoute()
  const router = useRouter()

  const tab = ref(0)
  const searchAddress = ref('')
  const propsAdr = ref('')
  const tokenAddress = ref('')
  const tokenName = ref('')
  const TokenInfo = ref<TokenDetailDisplay>({
    address: '',
    symbol: '',
    icon: defaultIcon,
  })
  const detailLoading = ref(false)
  const txLoading = ref(false)
  const pages = reactive({
    currentPage: 1,
    pageSize: 10,
    total: 0,
  })
  const Trades = ref<TokenTxRow[]>([])

  function mapDetail(data: TokenDetailApiData): TokenDetailDisplay {
    return {
      address: data.address,
      symbol: data.symbol,
      name: data.name as string | undefined,
      decimals: data.decimals as number | undefined,
      total: data.total_supply,
      total_formatted: data.total_supply_formatted as string | number | undefined,
      deploy_time: data.deploy_time,
      icon: defaultIcon,
      nickname: (data.name as string) || undefined,
      holders: typeof data.holders === 'number' ? data.holders : 0,
    }
  }

  function mapTransfer(item: TokenTransferItem): TokenTxRow {
    return {
      hash: item.tx_hash,
      tx_hash: item.tx_hash,
      from: item.from,
      to: item.to,
      block_time: item.block_time,
      value: item.value,
      value_formatted: item.value_formatted,
      token_symbol: item.token_symbol,
      success: true,
      is_para: false,
    }
  }

  async function loadTokenDetail(): Promise<void> {
    if (!tokenAddress.value) return
    detailLoading.value = true
    try {
      const response = await getTokenDetail(tokenAddress.value)
      if (response.code === 0 && response.data) {
        TokenInfo.value = mapDetail(response.data)
        tokenName.value = response.data.symbol || ''
      } else {
        ElMessage.error(response.message || '获取 Token 详情失败')
      }
    } catch {
      ElMessage.error('获取 Token 详情失败')
    } finally {
      detailLoading.value = false
    }
  }

  async function loadTransfers(): Promise<void> {
    if (!tokenAddress.value) return
    txLoading.value = true
    try {
      const params: { page: number; size: number; from?: string } = {
        page: pages.currentPage,
        size: pages.pageSize,
      }
      if (propsAdr.value) params.from = propsAdr.value

      const response = await getTokenTransfers(tokenAddress.value, params)
      if (response.code === 0 && response.data) {
        const { transfers, total } = response.data
        const list = transfers || []
        Trades.value = list.map((item) => mapTransfer(item))
        pages.total = total || 0
      } else {
        Trades.value = []
        pages.total = 0
        if (response.code !== 0) {
          ElMessage.error(response.message || '加载交易记录失败')
        }
      }
    } catch {
      Trades.value = []
      pages.total = 0
    } finally {
      txLoading.value = false
    }
  }

  watch(
    () => ({
      tokenAdr: route.query.tokenAdr,
      adr: route.query.adr,
    }),
    (q, prev) => {
      const { token, adr } = readTokenQuery(q as RouteTokenQuery)
      tokenAddress.value = token
      propsAdr.value = adr

      if (!token) {
        Trades.value = []
        pages.total = 0
        TokenInfo.value = { address: '', symbol: '', icon: defaultIcon }
        return
      }

      const prevPair = prev
        ? readTokenQuery(prev as RouteTokenQuery)
        : { token: '', adr: '' }
      const tokenChanged = prevPair.token !== token

      if (tokenChanged) {
        pages.currentPage = 1
        pages.pageSize = 10
        void loadTokenDetail().then(() => loadTransfers())
      } else if (prevPair.adr !== adr) {
        pages.currentPage = 1
        void loadTransfers()
      }
    },
    { immediate: true }
  )

  function searchAdr(): void {
    const next = searchAddress.value.trim()
    searchAddress.value = ''
    router.replace({
      path: '/tokenDetail',
      query: {
        tokenAdr: tokenAddress.value,
        ...(next ? { adr: next } : {}),
      },
    })
  }

  function tabChange(t: number): void {
    tab.value = t
    propsAdr.value = ''
    pages.currentPage = 1
    pages.pageSize = 10
    router.replace({
      path: '/tokenDetail',
      query: { tokenAdr: tokenAddress.value },
    })
    if (t === 1) {
      pages.total = TokenInfo.value.holders || 0
    }
    void loadTransfers()
  }

  function pageChange(page: number): void {
    pages.currentPage = page
    void loadTransfers()
  }

  function sizeChange(size: number): void {
    pages.pageSize = size
    pages.currentPage = 1
    void loadTransfers()
  }

  function clearAddrFilter(): void {
    propsAdr.value = ''
    router.replace({
      path: '/tokenDetail',
      query: { tokenAdr: tokenAddress.value },
    })
  }

  return {
    tab,
    searchAddress,
    propsAdr,
    tokenAddress,
    tokenName,
    TokenInfo,
    detailLoading,
    txLoading,
    pages,
    Trades,
    searchAdr,
    tabChange,
    pageChange,
    sizeChange,
    clearAddrFilter,
  }
}
